import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import * as XLSX from 'xlsx';
import './AdminDashboard.css';
import DiscountCodes from "../../components/DiscountCodes/DiscountCodes";

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState("submissions");
  const [userSubmissions, setUserSubmissions] = useState({});
  const [selectedSubmissions, setSelectedSubmissions] = useState(new Set());
  const [subscribers, setSubscribers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [offlineUsers, setOfflineUsers] = useState([]);
  const [filterType, setFilterType] = useState("all"); // all | selected | unselected
  const [registeredUsers, setRegisteredUsers] = useState([]);
// 🔑 Helper: generate 3-digit stable short ID from userId
const getShortIdNumber = (userId) => {
  if (!userId) return "000";
  let sum = 0;
  for (let i = 0; i < userId.length; i++) {
    sum += userId.charCodeAt(i);
  }
  let num = (sum % 999) + 1; // 1-999
  return num.toString().padStart(3, "0"); // "001", "042", etc.
};
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      window.location.href = "/login";
      return;
    }

    const decodedToken = jwtDecode(token);
    if (decodedToken.role !== "admin") {
      window.location.href = "/profile";
      return;
    }
const fetchRegisteredUsers = async () => {
  try {
    const token = localStorage.getItem("token");
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/user/registered-users`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setRegisteredUsers(res.data);
  } catch (err) {
    console.error("Error fetching registered users", err);
    setError("Failed to load registered users.");
  }
};

fetchRegisteredUsers();


    const fetchSubmissions = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/user/admin-dashboard`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
    
        const rawData = response.data;
    
        // Group by user ID
        const grouped = rawData.reduce((acc, submission) => {
          const uid = submission.user._id;
          if (!acc[uid]) {
            acc[uid] = { user: submission.user, submissions: [] };
          }
          acc[uid].submissions.push(submission);
          return acc;
        }, {});
    
        // Sort submissions by date (latest first)
        Object.keys(grouped).forEach(uid => {
          grouped[uid].submissions.sort(
            (a, b) => new Date(b.submissionDate) - new Date(a.submissionDate)
          );
        });
    
        setUserSubmissions(grouped);
    
        // Pre-select review
        const preselected = rawData
          .filter(s => s.selectedForReview === true)
          .map(s => s._id);
        setSelectedSubmissions(new Set(preselected));
      } catch (err) {
        console.error("Error fetching submissions", err);
        setError("Failed to load submissions.");
      } finally {
        setLoading(false);
      }
    };
    
    const fetchSubscribers = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/newsletter/getsubscribers`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSubscribers(res.data);
      } catch (err) {
        console.error("Error fetching subscribers", err);
        setError("Failed to load subscribers.");
      }
    };
    
    const fetchOfflineUsers = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/user/get-offline-users`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setOfflineUsers(res.data);
      } catch (err) {
        console.error("Error fetching offline users", err);
        setError("Failed to load offline users.");
      }
    };

    fetchOfflineUsers();
    fetchSubmissions();
    fetchSubscribers();
  }, []);

  // ✅ Toggle ALL submissions
  const handleSelectAll = () => {
    const allIds = Object.values(userSubmissions).flatMap(u => u.submissions.map(sub => sub._id));
    const allSelected = allIds.every(id => selectedSubmissions.has(id));

    if (allSelected) {
      setSelectedSubmissions(new Set());
    } else {
      setSelectedSubmissions(new Set(allIds));
    }
  };

  // ✅ Toggle all submissions for a user
  const handleSelectUser = (uid) => {
    const updated = new Set(selectedSubmissions);
    const userIds = userSubmissions[uid].submissions.map(sub => sub._id);

    const allSelected = userIds.every(id => updated.has(id));

    if (allSelected) {
      userIds.forEach(id => updated.delete(id));
    } else {
      userIds.forEach(id => updated.add(id));
    }

    setSelectedSubmissions(updated);
  };

  // ✅ Toggle single submission
  const handleSelectSubmission = (submissionId) => {
    const updated = new Set(selectedSubmissions);
    updated.has(submissionId) ? updated.delete(submissionId) : updated.add(submissionId);
    setSelectedSubmissions(updated);
  };

  // ✅ Save selected submissions
  const handleSaveSelected = async () => {
    const selectedIds = Array.from(selectedSubmissions);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/user/save-selected`,
        { submissionIds: selectedIds },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      alert('Selected submissions saved successfully!');
    } catch (err) {
      console.error("Error saving selected submissions", err);
      setError("Failed to save selected submissions.");
    }
  };

  // ✅ Export subscribers to Excel
  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(subscribers);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Subscribers");
    XLSX.writeFile(wb, "Newsletter_Subscribers.xlsx");
  };

    const exportRegisteredUsersToExcel = () => {
  const ws = XLSX.utils.json_to_sheet(registeredUsers);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Registered Users");
  XLSX.writeFile(wb, "Registered_Users.xlsx");
};
  const handleToggleVerify = async (userId, newStatus) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/user/toggle-verify/${userId}`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );

      // Update UI immediately
      setOfflineUsers(prev =>
        prev.map(user =>
          user._id === userId ? { ...user, verified: newStatus } : user
        )
      );
    } catch (err) {
      console.error("Error updating verification status", err);
      setError("Failed to update verification status.");
    }
  };

  return (
    <div className="admin-dashboard">
      <h1>Admin Dashboard</h1>

      {error && <div className="error-message">{error}</div>}

      <div className="tabs">
        <button className={activeTab === "submissions" ? "active" : ""} onClick={() => setActiveTab("submissions")}>
          Submissions
        </button>
        <button className={activeTab === "subscribers" ? "active" : ""} onClick={() => setActiveTab("subscribers")}>
          Subscribers
        </button>
        <button className={activeTab === "discountCodes" ? "active" : ""} onClick={() => setActiveTab("discountCodes")}>
          Discount Codes
        </button>
        <button className={activeTab === "offlineUsers" ? "active" : ""} onClick={() => setActiveTab("offlineUsers")}>
          Offline Users
        </button>
            <button
  className={activeTab === "registeredUsers" ? "active" : ""}
  onClick={() => setActiveTab("registeredUsers")}
>
  Registered Users
</button>
      </div>

      {/* Submissions Tab */}
      {activeTab === "submissions" && (
        <div>
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
          <div className="filter-buttons" style={{ display: "inline-flex", gap: "8px", marginLeft: "10px" }}>
            <button
              onClick={() => setFilterType("selected")}
              className={filterType === "selected" ? "active-filter" : ""}
            >
              Selected
            </button>
            <button
              onClick={() => setFilterType("unselected")}
              className={filterType === "unselected" ? "active-filter" : ""}
            >
              Unselected
            </button>
            <button
              onClick={() => setFilterType("all")}
              className={filterType === "all" ? "active-filter" : ""}
            >
              All
            </button>
          </div>

          {/* ✅ Master Select All */}
          <h2>
            <input
              type="checkbox"
              checked={
                Object.values(userSubmissions).flatMap(u => u.submissions).length > 0 &&
                Object.values(userSubmissions).flatMap(u => u.submissions).every(sub => selectedSubmissions.has(sub._id))
              }
              onChange={handleSelectAll}
            />
            Select All Users
          </h2>

          <button
            onClick={handleSaveSelected}
            style={{ marginLeft: '10px' }}
          >
            Save Selected Submissions
          </button>

          {loading ? (
            <div className="loading">Loading submissions...</div>
          ) : (
            <div>
              {Object.entries(userSubmissions)
                .filter(([uid, data]) => {
                  const user = data.user;
                  const searchStr = `${user.firstName} ${user.lastName} ${user.email} ${user.jobTitle} ${user.country} ${uid}`.toLowerCase();
                  return searchStr.includes(search.toLowerCase());
                })
                .filter(([uid, data]) => {
                  if (filterType === "selected") {
                    return data.submissions.some(sub => selectedSubmissions.has(sub._id));
                  } else if (filterType === "unselected") {
                    return data.submissions.every(sub => !selectedSubmissions.has(sub._id));
                  }
                  return true;
                })
                .map(([uid, data]) => {
                  const user = data.user;
                  const submissions = data.submissions;

                  return (
                    <div key={uid} className="submission-item">
                      <h2>
                        <input
                          type="checkbox"
                          checked={submissions.every(sub => selectedSubmissions.has(sub._id))}
                          onChange={() => handleSelectUser(uid)}
                        />
                        {user.firstName} {user.lastName} ({user.email}) – {user.jobTitle}
                        <br />
                        <small><strong>ID:</strong> {uid} | <strong>Country:</strong> {user.country || "N/A"} ||Short ID: {getShortIdNumber(uid)}</small>
                      </h2>

                      {user.teamMembers && (
                        <p style={{ marginLeft: "30px", fontSize: "0.9rem", color: "#444" }}>
                          👥 <strong>Team Members:</strong> {user.teamMembers}
                        </p>
                      )}

                      <div className="submission-row">
                        {submissions.slice(0, 3).map((sub, idx) => (
                          <div
                            key={sub._id}
                            className={`nested-submission ${selectedSubmissions.has(sub._id) ? 'selected' : ''}`}
                          >
                            <h3>{sub.title || `Submission ${idx + 1}`}</h3>
                            <p><strong>Date:</strong> {new Date(sub.submissionDate).toLocaleDateString()}</p>

                            <div className="file-container">
                              {sub.files && sub.files.map((file, index) => {
                                const isPDF = (file?.url || "").toLowerCase().endsWith(".pdf");
                                return (
                                  <div key={index} className="file-item">
                                    <a href={file.url} target="_blank" rel="noopener noreferrer">
                                      {isPDF ? (
                                        <div className="pdf-preview">📄 PDF File</div>
                                      ) : (
                                        <img
                                          src={file.url}
                                          alt={`File ${index + 1}`}
                                          className="submission-image"
                                        />
                                      )}
                                    </a>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      )}

      {/* Subscribers Tab */}
      {activeTab === "subscribers" && (
        <div>
          <button onClick={exportToExcel} disabled={subscribers.length === 0}>
            Export Subscribers to Excel
          </button>

          <div className="subscriber-list">
            <h2>Newsletter Subscribers</h2>
            <table>
              <thead>
                <tr>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Email</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {subscribers.map((sub, index) => (
                  <tr key={index}>
                    <td>{sub.firstName}</td>
                    <td>{sub.lastName}</td>
                    <td>{sub.email}</td>
                    <td>{sub.subscriptionStatus}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Discount Codes Tab */}
      {activeTab === "discountCodes" && (
        <div className="discount-codes-section">
          <DiscountCodes />
        </div>
      )}

      {/* Offline Users Tab */}
      {activeTab === "offlineUsers" && (
        <div className="offline-users-section">
          <h2>Offline Registered Users</h2>
          {offlineUsers.length === 0 ? (
            <p>No offline users found.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Country</th>
                  <th>Job Title</th>
                  <th>Proof of Payment</th>
                  <th>Verified</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {offlineUsers.map((user, index) => (
                  <tr key={index}>
                    <td>{user.firstName} {user.lastName}</td>
                    <td>{user.email}</td>
                    <td>{user.country}</td>
                    <td>{user.jobTitle || user.university || "N/A"}</td>
                    <td>
                      {user.proofOfPayment ? (
                        user.proofOfPayment.toLowerCase().endsWith(".pdf") ? (
                          <a
                            href={user.proofOfPayment}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            📄 View PDF
                          </a>
                        ) : (
                          <a
                            href={user.proofOfPayment}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              src={user.proofOfPayment}
                              alt="Proof of Payment"
                              style={{ width: "120px", borderRadius: "6px", objectFit: "contain" }}
                            />
                          </a>
                        )
                      ) : (
                        "No file"
                      )}
                    </td>
                    <td>{user.verified ? "✅" : "❌"}</td>
                    <td>
                      <button
                        onClick={() => handleToggleVerify(user._id, !user.verified)}
                        className={user.verified ? "unverify" : "verify"}
                      >
                        {user.verified ? "Unverify" : "Verify"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
        {activeTab === "registeredUsers" && (
  <div className="registered-users-section">
    <h2>Registered Users</h2>
    <button onClick={exportRegisteredUsersToExcel} disabled={registeredUsers.length === 0}>
      Export to Excel
    </button>
    {registeredUsers.length === 0 ? (
      <p>No users found.</p>
    ) : (
      <table>
        <thead>
          <tr>
            <th>First Name</th>
            <th>Last Name</th>
            <th>Email</th>
            <th>Country</th>
            <th>Job Title</th>
            <th>Amount Paid</th>
            <th>Discount Code Used</th>
          </tr>
        </thead>
        <tbody>
          {registeredUsers.map((user, idx) => (
            <tr key={idx}>
              <td>{user.firstName}</td>
              <td>{user.lastName}</td>
              <td>{user.email}</td>
              <td>{user.country || "N/A"}</td>
              <td>{user.jobTitle || user.university || "N/A"}</td>
              <td>{user.amountPaid || 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
)}

    </div>
  );
};

export default AdminDashboard;
